const REACT_APP_BFF_URL = process.env.REACT_APP_BFF_URL;

// Get upload policy for product images
export const getProductImageUploadPolicy = async (token, productId, fileExtension, contentType = "image/jpeg") => {
    const response = await fetch(`${REACT_APP_BFF_URL}/api/file-upload/product-image-policy`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            product_id: productId,
            file_extension: fileExtension,
            content_type: contentType
        }),
    });

    if (!response.ok) {
        throw new Error('Error al obtener la política de subida de imagen.');
    }

    return await response.json();
};

// Get upload policy for product documents
export const getProductDocumentUploadPolicy = async (token, productId, filename, contentType = "application/pdf") => {
    const response = await fetch(`${REACT_APP_BFF_URL}/api/file-upload/product-document-policy`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            product_id: productId,
            filename: filename,
            content_type: contentType
        }),
    });

    if (!response.ok) {
        throw new Error('Error al obtener la política de subida de documento.');
    }

    return await response.json();
};

// Get upload policy for landing images
export const getLandingImageUploadPolicy = async (token, filename, contentType = "image/jpeg") => {
    const response = await fetch(`${REACT_APP_BFF_URL}/api/file-upload/landing-image-policy`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            filename: filename,
            content_type: contentType
        }),
    });

    if (!response.ok) {
        throw new Error('Error al obtener la política de subida de imagen de landing.');
    }

    return await response.json();
};

// Get upload policy for user avatar
export const getUserAvatarUploadPolicy = async (token, fileExtension, contentType = "image/jpeg") => {
    const response = await fetch(`${REACT_APP_BFF_URL}/api/file-upload/user-avatar-policy`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            file_extension: fileExtension,
            content_type: contentType
        }),
    });

    if (!response.ok) {
        throw new Error('Error al obtener la política de subida de avatar.');
    }

    return await response.json();
};

// Get download URL for a file
export const getFileDownloadUrl = async (token, filePath, expirationMinutes = 60) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/api/file-upload/download-url?file_path=${encodeURIComponent(filePath)}&expiration_minutes=${expirationMinutes}`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        },
    });

    if (!response.ok) {
        throw new Error('Error al obtener la URL de descarga.');
    }

    return await response.json();
};

// Delete a file from bucket
export const deleteFileFromBucket = async (token, filePath) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/api/file-upload/file?file_path=${encodeURIComponent(filePath)}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`
        },
    });

    if (!response.ok) {
        throw new Error('Error al eliminar el archivo.');
    }

    return await response.json();
};

// Upload file using signed URL
export const uploadFileWithSignedUrl = async (signedUrl, file, contentType) => {
    const response = await fetch(signedUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': contentType,
        },
        body: file,
    });

    if (!response.ok) {
        throw new Error('Error al subir el archivo.');
    }

    return response;
};

// Helper function to get file extension from filename
export const getFileExtension = (filename) => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) return '';
    return filename.substring(lastDotIndex);
};

// Helper function to get content type from file extension
export const getContentTypeFromExtension = (extension) => {
    const contentTypes = {
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
        '.png': 'image/png',
        '.gif': 'image/gif',
        '.webp': 'image/webp',
        '.pdf': 'application/pdf',
        '.doc': 'application/msword',
        '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    };
    
    return contentTypes[extension.toLowerCase()] || 'application/octet-stream';
};

// Helper function to convert bucket paths to download URLs
export const getImageDownloadUrl = async (token, filePath) => {
    try {
        if (!filePath) return null;
        
        // If it's already a URL, return it
        if (filePath.startsWith('http')) {
            return filePath;
        }
        
        // If it's a bucket path, get signed URL
        const response = await getFileDownloadUrl(token, filePath, 60); // 60 minutes
        return response.download_url;
    } catch (error) {
        console.error('Error getting download URL:', error);
        return null;
    }
};

// Helper function to convert multiple bucket paths to download URLs
export const getMultipleImageDownloadUrls = async (token, filePaths) => {
    if (!Array.isArray(filePaths)) return [];
    
    const downloadUrls = [];
    for (const filePath of filePaths) {
        const url = await getImageDownloadUrl(token, filePath);
        if (url) {
            downloadUrls.push(url);
        }
    }
    return downloadUrls;
}; 